import { defineStore } from 'pinia';
import { useApi } from '@/lib/api';
import { useGlobalStore } from '@/store/global-store-base';
import { computed } from 'vue';
import type { ProductDto, SizeSystemDto, SizeSystemDtoVersion } from '@/ApiClient';
import { Gender } from '@/ApiClient';
import { useHierarchy } from '@/store/hierarchy.ts';
import { useUser } from '@/store/user';

// The default-size range cells to prefill for the given genders (gender matches, else gender-agnostic),
// kept in range order. Used directly by the 2D selection grid; the 1D path uses the value form below.
// Pure (depends only on its arguments) so it can be unit-tested and run before any slider mounts.
export function getDefaultSizeCellsForGenders(sizeSystemVersion: SizeSystemDtoVersion, genderKeys: Gender[]) {
  const genderMatches = sizeSystemVersion.defaultSizes.filter((ds) => genderKeys.includes(ds.gender!));
  const chosen = genderMatches.length ? genderMatches : sizeSystemVersion.defaultSizes.filter((ds) => !ds.gender);
  return sizeSystemVersion.range.filter((s) => chosen.some((ds) => ds.sizeId === s.id || ds.value === s.value));
}

// The default sizes configured specifically for the given genders, in range order, WITHOUT the
// gender-agnostic fallback. Used for gender deltas: adding a gender pulls in exactly that gender's
// sizes (and a system with only gender-agnostic defaults has no per-gender delta).
export function getPerGenderSizeValues(sizeSystemVersion: SizeSystemDtoVersion, genderKeys: Gender[]): string[] {
  const wanted = sizeSystemVersion.defaultSizes.filter((ds) => ds.gender != null && genderKeys.includes(ds.gender));
  return sizeSystemVersion.range
    .filter((s) => wanted.some((ds) => ds.sizeId === s.id || ds.value === s.value))
    .map((s) => s.value);
}

// Single source of truth for the default sizes to prefill when a size system (version) is assigned.
// Returns the size values for the given genders (gender matches, else gender-agnostic), in range
// order. Works for both 1D and 2D versions and needs no slider, so it can run before the slider mounts.
export function getDefaultSizeValuesForGenders(
  sizeSystemVersion: SizeSystemDtoVersion,
  genderKeys: Gender[]
): string[] {
  // A single-size version is always fully selected for convenience.
  if (sizeSystemVersion.range.length === 1) return sizeSystemVersion.range.map((s) => s.value);

  return getDefaultSizeCellsForGenders(sizeSystemVersion, genderKeys).map((s) => s.value);
}

// The selection a version should open with for the given genders: the sizes it suggests, else its
// whole range when it has no defaults configured at all, else null for "keep the current selection"
// (a gendered version none of the current genders match).
export function getInitialSizeValuesForVersion(
  sizeSystemVersion: SizeSystemDtoVersion,
  genderKeys: Gender[]
): string[] | null {
  const suggested = getDefaultSizeValuesForGenders(sizeSystemVersion, genderKeys);
  if (suggested.length) return suggested;

  if (sizeSystemVersion.defaultSizes.length) return null;

  return sizeSystemVersion.range.map((s) => s.value);
}

export const useSizeSystems = defineStore('size-systems', () => {
  const api = useApi();
  const base = useGlobalStore({
    load: api.sizeSystem_GetSizeSystems,
    dto: 'SizeSystemDto',
    create: api.sizeSystem_AddSizeSystem,
    update: api.sizeSystem_UpdateSizeSystem,
    delete: api.sizeSystem_DeleteSizeSystem,
    usage: api.sizeSystem_CountUsages,
    signalR: 'SizeSystemUpdate',
  });

  const allIds = computed(() => base.loaded.value.map((sizeSystem) => sizeSystem.id));

  function getSizeSystemFromSizeSystemVersionId(id: number) {
    return base.loaded.value.find((sizeSystem) => sizeSystem.versions.some((version) => version.id === id));
  }

  function getSizeSystemVersionFromId(id: number) {
    return base.loaded.value.flatMap((sizeSystem) => sizeSystem.versions).find((version) => version.id === id);
  }

  function getSizeSystemAndVersionFromSizeId(sizeId: number) {
    const systemAndVersion = base.loaded.value
      .flatMap((sizeSystem) => sizeSystem.versions.map((version) => ({ sizeSystem, version })))
      .find(({ version }) => version.range.some((s) => s.id === sizeId));

    if (!systemAndVersion) return null;

    const size = systemAndVersion.version.range.find((s) => s.id === sizeId);

    return size ? { ...systemAndVersion, size: size.value } : null;
  }

  function getDefaultVersionOfSizeSystem(sizeSystem: SizeSystemDto) {
    // TODO: REVIEW - should be based on some property instead of just the first one?
    return sizeSystem?.versions[0];
  }

  // Returns a formatted string representing the range of sizes.
  // examples: S-L, 36-42, 2-14, 2-14*
  function formatRange(sizes: string[], sizeSystemVersionId?: number) {
    // Callers pass one size value per SKU, so the same size can repeat (e.g. one group spanning the
    // same size across several colors). Collapse to distinct values so a single distinct size renders
    // as "M" rather than "M-M".
    const distinctSizes = [...new Set(sizes)];
    if (!distinctSizes.length) return;

    if (distinctSizes.length === 1) return distinctSizes[0];

    const sizeSystemVersion = sizeSystemVersionId ? getSizeSystemVersionFromId(sizeSystemVersionId) : undefined;

    const sorted = sizeSystemVersion
      ? [...distinctSizes].sort(
          (a, b) =>
            sizeSystemVersion.range.findIndex((s) => s.value === a) -
            sizeSystemVersion.range.findIndex((s) => s.value === b)
        )
      : distinctSizes;

    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    let result = `${first}-${last}`;

    if (!sizeSystemVersion) return result;

    const firstSizeIndex = sizeSystemVersion.range.findIndex((size) => size.value === first);
    const lastSizeIndex = sizeSystemVersion.range.findIndex((size) => size.value === last);
    const range = sizeSystemVersion.range.slice(firstSizeIndex, lastSizeIndex + 1);

    const hasMissingSizes = range.some((size) => !sorted.includes(size.value));
    if (hasMissingSizes) result = result + '*';

    return result;
  }

  const sizeSystemsAvailableOnAllNodes = computed(() => {
    const allRestrictedSizeSystems = useHierarchy().fullNodes.flatMap((node) => node.sizeSystemIds);
    return base.loaded.value.filter((sizeSystem) => !allRestrictedSizeSystems.includes(sizeSystem.id));
  });

  function getSizeValueById(sizeSystemVersionSizeId: number): string {
    const result = getSizeSystemAndVersionFromSizeId(sizeSystemVersionSizeId);
    return result?.size ?? '';
  }

  async function versionUsage(sizeSystemId: number, versionId: number) {
    const brandId = useUser().brandId;
    return await api.sizeSystem_CountVersionUsages(brandId, sizeSystemId, versionId);
  }

  return {
    ...base,
    allIds,
    getSizeSystemFromSizeSystemVersionId,
    getSizeSystemVersionFromId,
    getSizeSystemAndVersionFromSizeId,
    getSizeValueById,
    sizeSystemsAvailableOnAllNodes,
    getDefaultVersionOfSizeSystem,
    getDefaultSizeValuesForGenders,
    getDefaultSizeCellsForGenders,
    getPerGenderSizeValues,
    formatRange,

    versionUsage,

    //TODO: This is just to match old JS code - should just use the base methods directly
    getSizeSystemFromId: base.getById,
    update: (inSizeSystem: SizeSystemDto) => base.update(inSizeSystem.id, inSizeSystem),
    delete: ({ sizeSystemId }: { sizeSystemId: number }) => base.delete(sizeSystemId),
  };
});
